import React, { useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Popover,
  PopoverOrigin,
  Tooltip
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import HideSourceIcon from '@mui/icons-material/HideSourceSharp';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useFormContext } from 'react-hook-form';
import EditIcon from '@mui/icons-material/Edit';
import { TimeSignal } from 'graphql/types';
import { NotesActions, useTimeSignalActions } from '../../notesSelector/hooks';
import { useNotesFilterContext } from '../../NotesFilterContextProvider';
import { GroupClassification, useGroupClassification } from './hooks';

const useStyles = makeStyles((theme) => ({
  root: {
    pointerEvents: 'none'
  },
  actions: {
    fontSize: theme.typography.body1.fontSize,
    margin: theme.spacing(0, 1, 0, 1),
    textTransform: 'none',
    color: theme.palette.grey[700],
    fill: theme.palette.grey[700],
    '&:hover': {
      color: theme.palette.primary.main,
      fill: theme.palette.primary.main
    }
  },
  paper: {
    borderRadius: theme.spacing(2.5),
    pointerEvents: 'auto',
    translate: `${theme.spacing(-5)} ${theme.spacing(-4.25)}`
  }
}));

const useTooltipStyles = makeStyles((theme) => ({
  popper: {
    zIndex: theme.zIndex.tooltip + 2
  }
}));

const anchorOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right'
};
const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right'
};

type NotesListLabelGroupQuickActionsProps = {
  groupKey: string;
  notes: TimeSignal[];
  onPopoverOpen: (
    event: React.MouseEvent<HTMLElement> | null,
    overrideElem?: HTMLElement | null
  ) => void;
  onPopoverClose: () => void;
  anchorEl: HTMLElement | null;
  isPopoverOpen: boolean;
  openGroupEditDialog: () => void;
  isUncategorizedGroup: boolean;
};
export const NotesListLabelGroupQuickActions: React.FC<NotesListLabelGroupQuickActionsProps> = ({
  groupKey,
  notes,
  onPopoverClose,
  onPopoverOpen,
  isPopoverOpen,
  anchorEl,
  openGroupEditDialog,
  isUncategorizedGroup
}: NotesListLabelGroupQuickActionsProps) => {
  const classes = useStyles();
  const tooltipClasses = useTooltipStyles();
  const popoverClasses = useMemo(
    () => ({
      paper: classes.paper
    }),
    [classes.paper]
  );

  const onPopoverOpenNoPropagation = useCallback(
    (event: React.MouseEvent<HTMLElement>, overrideEl?: HTMLElement | null) => {
      onPopoverOpen(event, overrideEl);
      event?.stopPropagation();
    },
    [onPopoverOpen]
  );
  const onPopoverCloseNoPropagation = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      onPopoverClose();
      event.stopPropagation();
    },
    [onPopoverClose]
  );
  const paperProps = useMemo(
    () => ({
      onMouseEnter: (event: React.MouseEvent<HTMLElement, MouseEvent>) =>
        onPopoverOpenNoPropagation(event, anchorEl),
      onMouseLeave: onPopoverCloseNoPropagation
    }),
    [onPopoverCloseNoPropagation, onPopoverOpenNoPropagation, anchorEl]
  );

  const {
    filterContext: { selectedClassification }
  } = useNotesFilterContext();
  const { setValue, unregister } = useFormContext();
  const { handleTimeSignalAction } = useTimeSignalActions();
  const actionCallback = useCallback(
    async (classificationTarget: NotesActions) => {
      await handleTimeSignalAction({
        notes: notes,
        updates: {
          classificationTarget: classificationTarget
        }
      });
      const ids = notes.map((note: TimeSignal) => `${note.date}_${note.id}`);
      ids.forEach((id: string) => {
        if (
          selectedClassification === 'All' &&
          classificationTarget !== 'Trash'
        ) {
          setValue(id, false);
        }

        if (
          selectedClassification !== 'All' &&
          selectedClassification !== classificationTarget
        ) {
          unregister(id);
        }
      });
    },
    [
      handleTimeSignalAction,
      notes,
      selectedClassification,
      setValue,
      unregister
    ]
  );
  const { groupClassification } = useGroupClassification(notes);
  const timeSignalActions = useMemo(
    () => [
      {
        testId: 'group-edit-button',
        hide: isUncategorizedGroup,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          openGroupEditDialog();
        },
        displayText: 'timeSignalActions.groupEdit',
        icon: <EditIcon />
      },
      {
        testId: 'inbox-button',
        hide:
          selectedClassification === 'Unclassified' ||
          (selectedClassification === 'All' &&
            groupClassification === GroupClassification.Unclassified),
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Unclassified');
        },
        displayText: 'timeSignalActions.inbox',
        icon: <MoveToInboxIcon />
      },
      {
        testId: 'private-button',
        hide:
          selectedClassification === 'Private' ||
          (selectedClassification === 'All' &&
            groupClassification === GroupClassification.Private),
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Private');
        },
        displayText: 'timeSignalActions.private',
        icon: <HideSourceIcon />
      },
      {
        testId: 'accept-button',
        hide:
          selectedClassification === 'Accepted' ||
          (selectedClassification === 'All' &&
            groupClassification === GroupClassification.Accepted),
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Accepted');
        },
        displayText: 'timeSignalActions.accept',
        icon: <ThumbUpIcon />
      },
      {
        testId: 'trash-button',
        hide: selectedClassification === 'Trash',
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Trash');
        },
        displayText: 'timeSignalActions.trash',
        icon: <DeleteIcon />
      },
      {
        testId: 'delete-forever-button',
        hide: selectedClassification !== 'Trash',
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('DeleteForever');
        },
        displayText: 'timeSignalActions.deleteForever',
        icon: <DeleteForeverIcon />
      }
    ],
    [
      isUncategorizedGroup,
      selectedClassification,
      groupClassification,
      openGroupEditDialog,
      actionCallback
    ]
  );

  return (
    <Popover
      open={Boolean(isPopoverOpen)}
      anchorEl={anchorEl}
      disableRestoreFocus
      className={classes.root}
      classes={popoverClasses}
      PaperProps={paperProps}
      anchorOrigin={anchorOrigin as PopoverOrigin}
      transformOrigin={transformOrigin as PopoverOrigin}
      transitionDuration={0}
    >
      <Box data-testid={`notes-list-label-group-quick-actions-${groupKey}`}>
        {timeSignalActions.map(
          (action) =>
            !action.hide && (
              <IconButton
                data-testid={`notes-list-label-group-quick-actions-${groupKey}-${action.testId}`}
                key={action.testId}
                className={classes.actions}
                size={'small'}
                color={'default'}
                onClick={action.handleClick}
                disabled={false}
              >
                <Tooltip
                  title={<FormattedMessage id={action.displayText} />}
                  classes={tooltipClasses}
                >
                  {action.icon}
                </Tooltip>
              </IconButton>
            )
        )}
      </Box>
    </Popover>
  );
};
